import { useState } from 'react'
import { useTheme } from '../lib/ThemeContext'
import { useLang } from '../lib/LangContext'
import { useAuth } from '../lib/AuthContext'
import { updateBoutique } from '../lib/api'
import { activerNotifications, notificationsActivees } from '../lib/notifications'
import { resetBoutique } from '../lib/api'

export default function Parametres() {
  const { theme, toggleTheme } = useTheme()
  const { t, lang, changeLang } = useLang()
  const { user, boutiqueId, boutique, signOut } = useAuth()

  const [editNom, setEditNom] = useState(false)
  const [notifActives, setNotifActives] = useState(Notification.permission === 'granted')
  const [notifLoading, setNotifLoading] = useState(false)
  const [nouveauNom, setNouveauNom] = useState('')
  const [savingNom, setSavingNom] = useState(false)
  const [nomMsg, setNomMsg] = useState(null)
  const [resetStep, setResetStep] = useState(0)
  // 0 = bouton initial
  // 1 = premier avertissement
  // 2 = confirmation finale avec texte à taper
  // 3 = en cours
  // 4 = succès

  const [confirmText, setConfirmText] = useState('')
  const [resetError, setResetError] = useState('')

  async function handleActiverNotifs() {
    setNotifLoading(true)
    const { success } = await activerNotifications(user.id)
    setNotifActives(success)
    setNotifLoading(false)
  }

  async function saveNom() {
    if (!nouveauNom.trim()) return
    setSavingNom(true)
    const { error } = await updateBoutique(user.id, { nom: nouveauNom.trim() })
    setSavingNom(false)
    if (error) { setNomMsg({ type: 'error', text: error.message }); return }
    setNomMsg({ type: 'success', text: lang === 'fr' ? '✓ Nom mis à jour !' : '✓ Name updated!' })
    setEditNom(false)
    setTimeout(() => setNomMsg(null), 3000)
    // Recharger la page pour mettre à jour le contexte
    window.location.reload()
  }

  const MOT_CONFIRM = lang === 'fr' ? 'SUPPRIMER' : 'DELETE'

  async function executeReset() {
    if (confirmText !== MOT_CONFIRM) {
      setResetError(lang === 'fr' ? `Tape exactement "${MOT_CONFIRM}" pour confirmer.` : `Type exactly "${MOT_CONFIRM}" to confirm.`)
      return
    }
    setResetStep(3)
    const { error } = await resetBoutique(boutiqueId)
    if (error) {
      setResetError(error.message)
      setResetStep(2)
      return
    }
    setResetStep(4)
  }

  return (
    <div className="main">
      <div className="page-title">{t('settings')}</div>

      {/* LANGUE */}
      <div style={{ background: 'var(--warm-white)', border: '1px solid var(--cream-dark)', borderRadius: 'var(--radius-lg)', padding: '1.25rem', marginBottom: '1rem', boxShadow: 'var(--shadow-sm)' }}>
        <div style={{ fontFamily: 'Fraunces, serif', fontSize: 16, color: 'var(--charcoal)', marginBottom: '1rem' }}>
          🌍 {t('language')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[{ code: 'fr', flag: '🇫🇷', label: 'Français' }, { code: 'en', flag: '🇬🇧', label: 'English' }].map(l => (
            <button key={l.code} onClick={() => changeLang(l.code)} style={{
              padding: '14px 12px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
              border: `2px solid ${lang === l.code ? 'var(--sage)' : 'var(--cream-dark)'}`,
              background: lang === l.code ? 'var(--sage-pale)' : 'var(--cream)',
              display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'DM Sans, sans-serif'
            }}>
              <span style={{ fontSize: 24 }}>{l.flag}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: lang === l.code ? 'var(--sage-dark)' : 'var(--charcoal)' }}>{l.label}</div>
                {lang === l.code && <div style={{ fontSize: 10, color: 'var(--sage)', marginTop: 2 }}>✓ {lang === 'fr' ? 'Actif' : 'Active'}</div>}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* THÈME */}
      <div style={{ background: 'var(--warm-white)', border: '1px solid var(--cream-dark)', borderRadius: 'var(--radius-lg)', padding: '1.25rem', marginBottom: '1rem', boxShadow: 'var(--shadow-sm)' }}>
        <div style={{ fontFamily: 'Fraunces, serif', fontSize: 16, color: 'var(--charcoal)', marginBottom: '1rem' }}>
          🎨 {t('appearance')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[
            { id: 'light', icon: '☀️', label: t('light_mode'), bg: '#F7F4EF' },
            { id: 'dark', icon: '🌙', label: t('dark_mode'), bg: '#1A1D1B' }
          ].map(th => (
            <button key={th.id} onClick={() => theme !== th.id && toggleTheme()} style={{
              padding: '14px 12px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
              border: `2px solid ${theme === th.id ? 'var(--sage)' : 'var(--cream-dark)'}`,
              background: theme === th.id ? 'var(--sage-pale)' : 'var(--cream)',
              display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'DM Sans, sans-serif'
            }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: th.bg, border: '1px solid var(--cream-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                {th.icon}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: theme === th.id ? 'var(--sage-dark)' : 'var(--charcoal)' }}>{th.label}</div>
                {theme === th.id && <div style={{ fontSize: 10, color: 'var(--sage)', marginTop: 2 }}>✓ {lang === 'fr' ? 'Actif' : 'Active'}</div>}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* INFOS BOUTIQUE */}
      <div style={{ background: 'var(--warm-white)', border: '1px solid var(--cream-dark)', borderRadius: 'var(--radius-lg)', padding: '1.25rem', marginBottom: '1rem', boxShadow: 'var(--shadow-sm)' }}>
        <div style={{ fontFamily: 'Fraunces, serif', fontSize: 16, color: 'var(--charcoal)', marginBottom: '0.75rem' }}>
          🏪 {lang === 'fr' ? 'Ma boutique' : 'My shop'}
        </div>

        {/* NOM DE LA BOUTIQUE */}
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            {lang === 'fr' ? 'Nom de la boutique' : 'Shop name'}
          </div>
          {!editNom ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--cream)', borderRadius: 'var(--radius-md)', padding: '10px 14px' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--charcoal)' }}>
                {boutique?.nom || '—'}
              </div>
              <button onClick={() => { setNouveauNom(boutique?.nom || ''); setEditNom(true) }} style={{
                padding: '4px 12px', borderRadius: 50, fontSize: 11, fontWeight: 500,
                border: '1px solid var(--sage-light)', background: 'var(--sage-pale)',
                color: 'var(--sage-dark)', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif'
              }}>
                ✏️ {lang === 'fr' ? 'Modifier' : 'Edit'}
              </button>
            </div>
          ) : (
            <div>
              <input
                type="text"
                value={nouveauNom}
                onChange={e => setNouveauNom(e.target.value)}
                placeholder={lang === 'fr' ? 'Nom de la boutique' : 'Shop name'}
                style={{ width: '100%', marginBottom: 8 }}
                autoFocus
              />
              {nomMsg && (
                <div className={nomMsg.type === 'success' ? 'success-msg' : 'error-msg'} style={{ marginBottom: 8 }}>
                  {nomMsg.text}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setEditNom(false); setNomMsg(null) }} style={{
                  flex: 1, padding: '10px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                  border: '1.5px solid var(--cream-dark)', background: 'var(--cream)',
                  color: 'var(--muted)', fontSize: 13, fontFamily: 'DM Sans, sans-serif'
                }}>
                  {lang === 'fr' ? 'Annuler' : 'Cancel'}
                </button>
                <button onClick={saveNom} disabled={savingNom || !nouveauNom.trim()} className="btn-primary" style={{ flex: 2 }}>
                  {savingNom ? (lang === 'fr' ? 'Enregistrement...' : 'Saving...') : (lang === 'fr' ? '✓ Enregistrer' : '✓ Save')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* EMAIL */}
        <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.8, borderTop: '1px solid var(--cream-dark)', paddingTop: '0.75rem' }}>
          <div><strong style={{ color: 'var(--charcoal)' }}>Email :</strong> {user?.email || '—'}</div>
        </div>
      </div>

      {/* ── NOTIFICATIONS ── */}
      <div style={{ background: 'var(--warm-white)', border: '1px solid var(--cream-dark)', borderRadius: 'var(--radius-lg)', padding: '1.25rem', marginBottom: '1rem', boxShadow: 'var(--shadow-sm)' }}>
        <div style={{ fontFamily: 'Fraunces, serif', fontSize: 16, color: 'var(--charcoal)', marginBottom: '0.75rem' }}>
          🔔 {lang === 'fr' ? 'Notifications' : 'Notifications'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1rem' }}>
          {lang === 'fr'
            ? 'Recevez des alertes sur votre téléphone pour les stocks faibles et les produits qui vont expirer.'
            : 'Receive alerts on your phone for low stocks and expiring products.'}
        </div>
        {notifActives ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--sage-pale)', borderRadius: 'var(--radius-md)', padding: '10px 14px' }}>
            <span style={{ fontSize: 18 }}>✅</span>
            <div style={{ fontSize: 13, color: 'var(--sage-dark)', fontWeight: 500 }}>
              {lang === 'fr' ? 'Notifications activées !' : 'Notifications enabled!'}
            </div>
          </div>
        ) : (
          <button onClick={handleActiverNotifs} disabled={notifLoading} className="btn-primary">
            {notifLoading
              ? (lang === 'fr' ? 'Activation...' : 'Activating...')
              : (lang === 'fr' ? '🔔 Activer les notifications' : '🔔 Enable notifications')}
          </button>
        )}
      </div>

      {/* ── ZONE DANGER : REMISE À ZÉRO ── */}
      <div style={{ background: 'var(--rose-pale)', border: '1.5px solid #DFA0A0', borderRadius: 'var(--radius-lg)', padding: '1.25rem', marginBottom: '1rem' }}>
        <div style={{ fontFamily: 'Fraunces, serif', fontSize: 16, color: 'var(--rose)', marginBottom: '0.5rem' }}>
          ⚠️ {lang === 'fr' ? 'Zone dangereuse' : 'Danger zone'}
        </div>

        {/* ÉTAPE 0 : BOUTON INITIAL */}
        {resetStep === 0 && (
          <>
            <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1rem' }}>
              {lang === 'fr'
                ? 'Remet ta boutique à zéro : supprime tous les produits, mouvements et historique. Cette action est irréversible.'
                : 'Reset your shop to zero: deletes all products, movements and history. This action is irreversible.'}
            </div>
            <button onClick={() => setResetStep(1)} style={{
              padding: '10px 16px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
              border: '1.5px solid var(--rose)', background: 'transparent',
              color: 'var(--rose)', fontSize: 13, fontFamily: 'DM Sans, sans-serif', fontWeight: 500
            }}>
              🗑️ {lang === 'fr' ? 'Remettre à zéro' : 'Reset everything'}
            </button>
          </>
        )}

        {/* ÉTAPE 1 : PREMIER AVERTISSEMENT */}
        {resetStep === 1 && (
          <div style={{ animation: 'slideUp 0.3s ease' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--rose)', marginBottom: '0.75rem' }}>
              🚨 {lang === 'fr' ? 'Es-tu vraiment sûr ?' : 'Are you really sure?'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--charcoal)', lineHeight: 1.8, marginBottom: '1rem' }}>
              {lang === 'fr' ? (
                <>
                  Cette action va <strong>définitivement supprimer</strong> :<br/>
                  ❌ Tous tes produits ({lang === 'fr' ? 'impossible à récupérer' : 'cannot be recovered'})<br/>
                  ❌ Tout l'historique des mouvements<br/>
                  ❌ Tous tes caissiers enregistrés<br/>
                  ✅ Ton compte et ton abonnement sont conservés
                </>
              ) : (
                <>
                  This action will <strong>permanently delete</strong>:<br/>
                  ❌ All your products (cannot be recovered)<br/>
                  ❌ All movement history<br/>
                  ❌ All registered cashiers<br/>
                  ✅ Your account and subscription are kept
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setResetStep(0)} style={{
                flex: 1, padding: '11px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                border: '1.5px solid var(--cream-dark)', background: 'var(--warm-white)',
                color: 'var(--muted)', fontSize: 13, fontFamily: 'DM Sans, sans-serif'
              }}>
                {lang === 'fr' ? '← Annuler' : '← Cancel'}
              </button>
              <button onClick={() => setResetStep(2)} style={{
                flex: 1, padding: '11px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                border: 'none', background: 'var(--rose)',
                color: 'white', fontSize: 13, fontFamily: 'DM Sans, sans-serif', fontWeight: 600
              }}>
                {lang === 'fr' ? 'Je comprends, continuer →' : 'I understand, continue →'}
              </button>
            </div>
          </div>
        )}

        {/* ÉTAPE 2 : CONFIRMATION FINALE */}
        {resetStep === 2 && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--rose)', marginBottom: '0.75rem' }}>
              🔐 {lang === 'fr' ? 'Confirmation finale' : 'Final confirmation'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--charcoal)', marginBottom: '0.75rem', lineHeight: 1.7 }}>
              {lang === 'fr'
                ? <>Pour confirmer, tape exactement <strong style={{ color: 'var(--rose)', letterSpacing: 2 }}>{MOT_CONFIRM}</strong> dans le champ ci-dessous :</>
                : <>To confirm, type exactly <strong style={{ color: 'var(--rose)', letterSpacing: 2 }}>{MOT_CONFIRM}</strong> in the field below:</>}
            </div>
            <input
              type="text"
              placeholder={MOT_CONFIRM}
              value={confirmText}
              onChange={e => { setConfirmText(e.target.value.toUpperCase()); setResetError('') }}
              style={{
                width: '100%', padding: '11px 14px', marginBottom: '0.75rem',
                border: `2px solid ${confirmText === MOT_CONFIRM ? 'var(--rose)' : 'var(--cream-dark)'}`,
                borderRadius: 'var(--radius-md)', fontSize: 16, fontFamily: 'monospace',
                letterSpacing: 3, textAlign: 'center', background: 'var(--warm-white)',
                color: 'var(--charcoal)', outline: 'none'
              }}
            />
            {resetError && <div className="error-msg" style={{ marginBottom: 8 }}>{resetError}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setResetStep(0); setConfirmText(''); setResetError('') }} style={{
                flex: 1, padding: '11px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                border: '1.5px solid var(--cream-dark)', background: 'var(--warm-white)',
                color: 'var(--muted)', fontSize: 13, fontFamily: 'DM Sans, sans-serif'
              }}>
                {lang === 'fr' ? 'Annuler' : 'Cancel'}
              </button>
              <button
                onClick={executeReset}
                disabled={confirmText !== MOT_CONFIRM}
                style={{
                  flex: 1, padding: '11px', borderRadius: 'var(--radius-md)', cursor: confirmText === MOT_CONFIRM ? 'pointer' : 'not-allowed',
                  border: 'none', background: confirmText === MOT_CONFIRM ? 'var(--rose)' : '#DFA0A0',
                  color: 'white', fontSize: 13, fontFamily: 'DM Sans, sans-serif', fontWeight: 600
                }}>
                🗑️ {lang === 'fr' ? 'Tout supprimer' : 'Delete everything'}
              </button>
            </div>
          </div>
        )}

        {/* ÉTAPE 3 : EN COURS */}
        {resetStep === 3 && (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
            <div style={{ fontSize: 13, color: 'var(--rose)', fontWeight: 500 }}>
              {lang === 'fr' ? 'Suppression en cours...' : 'Deleting...'}
            </div>
          </div>
        )}

        {/* ÉTAPE 4 : SUCCÈS */}
        {resetStep === 4 && (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>✅</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sage-dark)', marginBottom: 6 }}>
              {lang === 'fr' ? 'Boutique remise à zéro !' : 'Shop reset successfully!'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: '1rem' }}>
              {lang === 'fr' ? 'Tu peux maintenant recommencer à zéro.' : 'You can now start fresh.'}
            </div>
            <button onClick={() => { setResetStep(0); setConfirmText(''); window.location.reload() }} className="btn-primary">
              {lang === 'fr' ? 'Recommencer →' : 'Start fresh →'}
            </button>
          </div>
        )}
      </div>

      {/* INFOS APP */}
      <div style={{ background: 'var(--warm-white)', border: '1px solid var(--cream-dark)', borderRadius: 'var(--radius-lg)', padding: '1.25rem', boxShadow: 'var(--shadow-sm)' }}>
        <div style={{ fontFamily: 'Fraunces, serif', fontSize: 16, color: 'var(--charcoal)', marginBottom: '0.75rem' }}>
          ℹ️ {lang === 'fr' ? 'À propos' : 'About'}
        </div>
        <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.8 }}>
          <div>StockO v1.0</div>
          <div>{lang === 'fr' ? 'Gestion de stock pour petites boutiques' : 'Stock management for small shops'}</div>
          <div style={{ marginTop: 8, color: 'var(--sage)', fontSize: 12 }}>Made with 💚 for Africa</div>
        </div>
      </div>
    </div>
  )
}
